export const THEME_CACHE_KEY = "tactic-theme-cache";

export interface ColorSetting {
  name: string;
  vars: string[];
  id: string;
  category: ColorCategory;
}

export enum ColorCategory {
  General = "General",
  Sidebar = "Sidebar",
  Card = "Card",
  Overlay = "Overlay",
  Border = "Border",
  Primary = "Primary",
  Secondary = "Secondary",
  Tertiary = "Tertiary",
  Disabled = "Disabled",
  Map = "Map"
}

export const themeColors: ColorSetting[] = [
  // General
  {
    id: "foreground",
    name: "Foreground",
    vars: ["--color-foreground"],
    category: ColorCategory.General
  },
  {
    id: "background",
    name: "Background",
    vars: ["--v2-background"],
    category: ColorCategory.General
  },

  // Sidebar
  {
    id: "sidebarBackground",
    name: "Sidebar Background",
    vars: ["--v2-sidebar-background"],
    category: ColorCategory.Sidebar
  },
  {
    id: "sidebarForeground",
    name: "Sidebar Foreground",
    vars: ["--color-primary-bg"],
    category: ColorCategory.Sidebar
  },
  {
    id: "sidebarIcons",
    name: "Sidebar Icons",
    vars: ["--v2-sidebar-icons"],
    category: ColorCategory.Sidebar
  },
  {
    id: "sidebarIconsSelected",
    name: "Sidebar Icons Selected / Hover",
    vars: ["--v2-sidebar-icons-selected"],
    category: ColorCategory.Sidebar
  },

  // Card
  {
    id: "cardBackground",
    name: "Card Background",
    vars: ["--color-card-background"],
    category: ColorCategory.Card
  },
  {
    id: "cardForeground",
    name: "Card Foreground",
    vars: ["--color-card-foreground"],
    category: ColorCategory.Card
  },

  // Overlay
  {
    id: "overlayBackground",
    name: "Overlay Background",
    vars: ["--color-overlay-background"],
    category: ColorCategory.Overlay
  },
  {
    id: "overlayForeground",
    name: "Overlay Foreground",
    vars: ["--color-overlay-foreground"],
    category: ColorCategory.Overlay
  },

  // Border
  {
    id: "borderPrimary",
    name: "Border Primary",
    vars: ["--color-border-primary"],
    category: ColorCategory.Border
  },
  {
    id: "borderSecondary",
    name: "Border Secondary",
    vars: ["--color-border-secondary"],
    category: ColorCategory.Border
  },
  {
    id: "borderSidebar",
    name: "Border Sidebar",
    vars: ["--color-border-sidebar"],
    category: ColorCategory.Border
  },

  // Primary
  {
    id: "primary",
    name: "Primary",
    vars: ["--color-primary"],
    category: ColorCategory.Primary
  },
  {
    id: "primaryForeground",
    name: "Primary Foreground",
    vars: ["--color-primary-text", "--color-primary-foreground"],
    category: ColorCategory.Primary
  },
  {
    id: "primaryHover",
    name: "Primary Hover",
    vars: ["--color-primary-hover"],
    category: ColorCategory.Primary
  },
  /*{
        id: 'primaryHoverForeground',
        name: 'Primary Hover Foreground',
        vars: ['--color-primary-hover-foreground'],
        category: ColorCategory.Primary
      },*/

  // Secondary
  {
    id: "secondary",
    name: "Secondary",
    vars: ["--color-secondary"],
    category: ColorCategory.Secondary
  },
  /*{
        id: 'secondaryHover',
        name: 'Secondary Hover',
        vars: ['--color-secondary-hover'],
        category: ColorCategory.Secondary
      },*/
  {
    id: "secondaryForeground",
    name: "Secondary Foreground",
    vars: ["--color-secondary-foreground"],
    category: ColorCategory.Secondary
  },
  /*{
        id: 'secondaryHoverForeground',
        name: 'Secondary Hover Foreground',
        vars: ['--color-secondary-hover-foreground'],
        category: ColorCategory.Secondary
      },*/

  // Tertiary
  {
    id: "tertiary",
    name: "Tertiary",
    vars: ["--color-tertiary"],
    category: ColorCategory.Tertiary
  },
  {
    id: "tertiaryForeground",
    name: "Tertiary Foreground",
    vars: ["--color-tertiary-foreground"],
    category: ColorCategory.Tertiary
  },
  /*{
        id: 'tertiaryHover',
        name: 'Tertiary Hover',
        vars: ['--color-tertiary-hover'],
        category: ColorCategory.Tertiary
      },*/
  /*{
        id: 'tertiaryHoverForeground',
        name: 'Tertiary Hover Foreground',
        vars: ['--color-tertiary-hover-foreground'],
        category: ColorCategory.Tertiary
      },*/

  // Disabled
  {
    id: "disabledBackground",
    name: "Disabled Background",
    vars: ["--color-disabled-background"],
    category: ColorCategory.Disabled
  },
  {
    id: "disabledForeground",
    name: "Disabled Foreground",
    vars: ["--color-disabled-foreground"],
    category: ColorCategory.Disabled
  },

  // Map
  {
    id: "mapSelection",
    name: "Map Selection",
    vars: ["--color-map-selection"],
    category: ColorCategory.Map
  },
  {
    id: "mapAvailable",
    name: "Map Available",
    vars: ["--color-map-available"],
    category: ColorCategory.Map
  },
  {
    id: "mapNotAvailable",
    name: "Map Not Available",
    vars: ["--color-map-not-available"],
    category: ColorCategory.Map
  },
  {
    id: "mapReserved",
    name: "Map Reserved",
    vars: ["--color-map-reserved"],
    category: ColorCategory.Map
  },
  {
    id: "mapSelf",
    name: "Map Self",
    vars: ["--color-map-self"],
    category: ColorCategory.Map
  }
];

export const applyThemeColors = (colors: Record<string, string>) => {
  themeColors.forEach((colorSetting) => {
    const colorValue = colors[colorSetting.id];
    if (colorValue) {
      colorSetting.vars.forEach((varName) => {
        document.body.style.setProperty(varName, colorValue);
      });
    }
  });
};

// "Mona Sans" was the previous hard-coded default font. A stored value of
// "Mona Sans" is now treated as "no explicit choice" so the app falls back to
// the app-wide Inter default from index.css instead of forcing Mona.
export const LEGACY_DEFAULT_FONT = "Mona Sans";

/** A theme font counts as an explicit choice only if it's set and not the legacy default. */
export const isExplicitThemeFont = (font?: string | null): font is string =>
  !!font && font !== LEGACY_DEFAULT_FONT;

export const applyThemeFont = (font: string) => {
  const styleId = "tactic-global-font-style";
  let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;

  // Legacy "Mona Sans" (or an empty value) is treated as no explicit choice:
  // clear any injected override and drop the cached font so the CSS Inter
  // default applies here and on the next page load's boot script.
  if (!isExplicitThemeFont(font)) {
    if (styleEl) styleEl.innerHTML = "";
    try {
      const existing = JSON.parse(localStorage.getItem(THEME_CACHE_KEY) || "{}");
      delete existing.font;
      localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(existing));
    } catch {
      // Storage full or unavailable — non-critical
    }
    return;
  }

  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }

  // No `!important`: this is the app-wide default font, but the `.font-inter`
  // opt-out (which IS `!important`) must always win — on the element and on any
  // nested descendant — so a single `.font-inter` ancestor forces Inter. The
  // `:not(.font-mona)` guard likewise lets the (non-`!important`) `.font-mona`
  // opt-in keep Mona Sans even when a tenant font is applied.
  styleEl.innerHTML = `.tactic-v2.tactic-v2 *:not(text):not(tspan):not(.font-inter):not(.font-mona)  { font-family: ${font}; }`;

  // Cache font to localStorage for instant application on next page load
  try {
    const existing = JSON.parse(localStorage.getItem(THEME_CACHE_KEY) || "{}");
    localStorage.setItem(
      THEME_CACHE_KEY,
      JSON.stringify({ ...existing, font })
    );
  } catch {
    // Storage full or unavailable — non-critical
  }
};

// --- Simplified Theme Settings ---

export type SimplifiedColorGroup = "Brand" | "Sidebar" | "Layout";

export interface SimplifiedColorConfig {
  id: string;
  label: string;
  group: SimplifiedColorGroup;
  cssVars: string[];
}

export const SIMPLIFIED_COLORS: SimplifiedColorConfig[] = [
  {
    id: "primary",
    label: "Primary Color",
    group: "Brand",
    cssVars: ["--color-primary"]
  },
  {
    id: "secondary",
    label: "Secondary Color",
    group: "Brand",
    cssVars: ["--color-secondary"]
  },
  {
    id: "foreground",
    label: "Text Color",
    group: "Brand",
    cssVars: ["--color-foreground", "--color-ink", "--color-tertiary"]
  },
  {
    id: "sidebarBackground",
    label: "Background",
    group: "Sidebar",
    cssVars: [
      "--v2-sidebar-background",
      "--color-v2-sidebar",
      "--color-sidebar"
    ]
  },
  {
    id: "sidebarIcons",
    label: "Icons",
    group: "Sidebar",
    cssVars: ["--v2-sidebar-icons", "--color-v2-sidebar-icons"]
  },
  {
    id: "sidebarIconsSelected",
    label: "Icons (Active)",
    group: "Sidebar",
    cssVars: [
      "--v2-sidebar-icons-selected",
      "--color-v2-sidebar-icons-selected"
    ]
  },
  {
    id: "background",
    label: "Page Background",
    group: "Layout",
    cssVars: ["--v2-background", "--color-v2-background"]
  },
  {
    id: "cardBackground",
    label: "Card Background",
    group: "Layout",
    cssVars: [
      "--v2-card-background",
      "--color-card-background",
      "--color-v2-card"
    ]
  },
  {
    id: "borderColor",
    label: "Border Color",
    group: "Layout",
    cssVars: ["--border", "--color-border-primary"]
  }
];

export const SIMPLIFIED_COLOR_GROUPS: SimplifiedColorGroup[] = [
  "Brand",
  "Sidebar",
  "Layout"
];

/** WCAG 2.0 relative luminance */
export const getRelativeLuminance = (hex: string): number => {
  const h = hex.replace("#", "");
  const r = Number.parseInt(h.substring(0, 2), 16) / 255;
  const g = Number.parseInt(h.substring(2, 4), 16) / 255;
  const b = Number.parseInt(h.substring(4, 6), 16) / 255;

  const toLinear = (c: number) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

/** Returns a contrast text color for a given background */
export const getContrastColor = (bgHex: string): string => {
  const luminance = getRelativeLuminance(bgHex);
  return luminance > 0.179 ? "#171717" : "#FAFAFA";
};

/** Generates a subtle border color from a background: darkens light backgrounds, lightens dark ones */
export const generateCardBorderColor = (bgHex: string): string => {
  const luminance = getRelativeLuminance(bgHex);
  const h = bgHex.replace("#", "");
  const r = Number.parseInt(h.substring(0, 2), 16);
  const g = Number.parseInt(h.substring(2, 4), 16);
  const b = Number.parseInt(h.substring(4, 6), 16);

  // Mix towards black for light backgrounds, towards white for dark ones
  const target = luminance > 0.5 ? 0 : 255;
  const amount = luminance > 0.5 ? 0.12 : 0.14;

  const mix = (channel: number) =>
    Math.min(
      255,
      Math.max(0, Math.round(channel + (target - channel) * amount))
    );

  const toHex = (n: number) => n.toString(16).padStart(2, "0");

  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
};

/** Generates a muted/lighter variant of a color by mixing it towards gray */
export const generateMutedColor = (hex: string, amount = 0.5): string => {
  const h = hex.replace("#", "");
  const r = Number.parseInt(h.substring(0, 2), 16);
  const g = Number.parseInt(h.substring(2, 4), 16);
  const b = Number.parseInt(h.substring(4, 6), 16);

  const mix = (channel: number) =>
    Math.min(255, Math.max(0, Math.round(channel + (0x80 - channel) * amount)));

  const toHex = (n: number) => n.toString(16).padStart(2, "0");

  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
};

/** Generates a hover variant of a color: darkens light colors, lightens dark ones */
export const generateHoverColor = (hex: string): string => {
  const luminance = getRelativeLuminance(hex);
  const h = hex.replace("#", "");
  const r = Number.parseInt(h.substring(0, 2), 16);
  const g = Number.parseInt(h.substring(2, 4), 16);
  const b = Number.parseInt(h.substring(4, 6), 16);

  // Dark colors get lighter on hover, light colors get darker
  const factor = luminance > 0.3 ? -0.15 : 0.2;

  const adjust = (channel: number) =>
    Math.min(255, Math.max(0, Math.round(channel + (255 - channel) * factor)));

  const toHex = (n: number) => n.toString(16).padStart(2, "0");

  return `#${toHex(adjust(r))}${toHex(adjust(g))}${toHex(adjust(b))}`;
};

/** Derives all theme color IDs (simplified + auto-generated) for saving to API */
export const deriveAllColors = (
  simplified: Record<string, string>
): Record<string, string> => {
  const result = { ...simplified };

  if (simplified.primary) {
    result.primaryForeground = getContrastColor(simplified.primary);
    result.primaryHover = generateHoverColor(simplified.primary);
  }
  if (simplified.secondary) {
    result.secondaryForeground = getContrastColor(simplified.secondary);
  }
  if (simplified.sidebarBackground) {
    result.sidebarForeground = getContrastColor(simplified.sidebarBackground);
  }
  if (simplified.cardBackground) {
    result.cardForeground = getContrastColor(simplified.cardBackground);
    result.cardBorder = generateCardBorderColor(simplified.cardBackground);
  }
  // Map borderColor to borderPrimary for backward compatibility
  if (simplified.borderColor) {
    result.borderPrimary = simplified.borderColor;
  }

  return result;
};

/** Applies simplified theme colors to the page (both via themeColors array and direct CSS vars) */
export const applySimplifiedThemeColors = (
  simplified: Record<string, string>
): void => {
  const derived = deriveAllColors(simplified);

  // Apply via standard themeColors array (handles standard ID->CSS var mappings)
  applyThemeColors(derived);

  // Apply via simplified config CSS vars on both :root and body.
  // Setting on documentElement overrides @theme token values defined on :root.
  // Setting on body overrides .tactic-v2 class definitions.
  for (const config of SIMPLIFIED_COLORS) {
    const value = simplified[config.id];
    if (value) {
      for (const cssVar of config.cssVars) {
        document.documentElement.style.setProperty(cssVar, value);
        document.body.style.setProperty(cssVar, value);
      }
    }
  }

  // Apply derived foreground color variants
  if (simplified.foreground) {
    const fg = simplified.foreground;
    const neutralOverrides: [string, string][] = [
      ["--color-muted-foreground", generateMutedColor(fg, 0.65)],
      ["--color-neutral-500", generateMutedColor(fg, 0.65)],
      ["--color-neutral-600", generateMutedColor(fg, 0.55)],
      ["--color-neutral-700", generateMutedColor(fg, 0.5)],
      ["--color-neutral-800", fg],
      ["--color-neutral-900", fg],
      ["--color-neutral-1000", fg],
      ["--color-secondary-text", fg]
    ];
    for (const [cssVar, value] of neutralOverrides) {
      document.documentElement.style.setProperty(cssVar, value);
      document.body.style.setProperty(cssVar, value);
    }
  }

  // Apply derived card border color
  if (derived.cardBorder) {
    document.documentElement.style.setProperty(
      "--color-card-border",
      derived.cardBorder
    );
    document.body.style.setProperty("--color-card-border", derived.cardBorder);
  }

  // Apply derived hover color
  if (derived.primaryHover) {
    document.documentElement.style.setProperty(
      "--color-primary-hover",
      derived.primaryHover
    );
    document.body.style.setProperty(
      "--color-primary-hover",
      derived.primaryHover
    );
  }

  // Cache to localStorage for instant application on next page load
  try {
    const existing = JSON.parse(localStorage.getItem(THEME_CACHE_KEY) || "{}");
    localStorage.setItem(
      THEME_CACHE_KEY,
      JSON.stringify({ ...existing, colors: simplified })
    );
  } catch {
    // Storage full or unavailable — non-critical
  }
};

/** Extracts only the 9 simplified color keys from a full saved colors record */
export const extractSimplifiedColors = (
  saved: Record<string, string>
): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const config of SIMPLIFIED_COLORS) {
    if (saved[config.id]) {
      result[config.id] = saved[config.id];
    }
  }

  // Backward compatibility: map old borderPrimary to new borderColor
  if (!result.borderColor && saved.borderPrimary) {
    result.borderColor = saved.borderPrimary;
  }

  return result;
};
